// HFP v3 design tokens — the ONE source for every rating/answer colour and
// display word (build instruction section 5.2 / 5.5; ticket TD-207).
// No rating or answer-level hex may live anywhere else. Colour is never the
// only cue: every chip renders its word alongside its colour.

// ---- area rating tokens (section 5.5) -------------------------------------
// Each token carries four roles: accent, chip fill, chip text, chip border.
export const RATING_TOKENS = {
  priority: { accent: '#e5484d', bg: '#fdeaea', fg: '#b3302c', border: '#f2b6b2' },
  support: { accent: '#f2a33c', bg: '#fef1e0', fg: '#8f5312', border: '#f3cfa0' },
  none: { accent: '#3a9b4e', bg: '#e8f4ea', fg: '#2b7038', border: '#bfe3b3' },
  strength: { accent: '#3a9b4e', bg: '#d3ecdd', fg: '#175c33', border: '#93cfae' },
  insufficient: { accent: '#b6bcc4', bg: '#eef0f3', fg: '#5f6773', border: '#d9dde3' },
  // Not applicable shares the Insufficient grey (distinct WORD, same token).
  notApplicable: { accent: '#b6bcc4', bg: '#eef0f3', fg: '#5f6773', border: '#d9dde3' },
};

// The environmental no-barrier outline chip: white fill, grey border — must not
// be confusable with the filled Insufficient grey.
export const ENV_NO_BARRIER_TOKEN = { accent: '#b6bcc4', bg: '#ffffff', fg: '#5f6773', border: '#b6bcc4' };

// ---- area rating words (section 5.2) ---------------------------------------
export const AREA_WORDS = {
  strength: 'Strength',
  none: 'No difficulty',
  support: 'Support need',
  priority: 'Priority area',
  insufficient: 'Insufficient data',
  notApplicable: 'Not applicable',
};

// Environmental areas read the parallel set (+ the two grey states above).
export const ENV_AREA_WORDS = {
  strength: 'Facilitators in place',
  none: 'No barriers',
  support: 'Barriers present',
  priority: 'Severe barriers',
  insufficient: 'Insufficient data',
  notApplicable: 'Not applicable',
};

// ---- statement-chip palette (section 5.2) ----------------------------------
// Keys are the data-borne tag_colour tokens from answer-options; the words come
// from each option's clinical_interpretation string, rendered verbatim. The 0
// level on environmental statements renders as an OUTLINE chip so it cannot be
// confused with Insufficient grey.
export const STATEMENT_CHIP_TOKENS = {
  green: { bg: '#e8f4ea', fg: '#2b7038', border: '#bfe3b3' },
  yellow: { bg: '#fdf6d8', fg: '#7a6414', border: '#ecd98a' },
  amber: { bg: '#fef1e0', fg: '#8f5312', border: '#f3cfa0' },
  red: { bg: '#fdeaea', fg: '#b3302c', border: '#f2b6b2' },
  grey: { bg: '#eef0f3', fg: '#5f6773', border: '#d9dde3' },
  // strength answers (+1 / +0.5) reuse the Strength area token
  strengthAnswer: { bg: '#d3ecdd', fg: '#175c33', border: '#93cfae' },
  envZeroOutline: { bg: '#ffffff', fg: '#5f6773', border: '#b6bcc4' },
};

// ---- provenance / profile treatments (sections 5.2, 5.5) -------------------
// RAISED BY PROFILE · PAIN SAFETY OVERRIDE · N SENSES RAISED BY PROFILE
export const PROVENANCE_FOOTER_STYLE = {
  fontSize: '8.5px',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#8a5a86',
};

export const MASKING_MARKER = {
  text: 'consider masking',
  title:
    'Reacts strongly but puts up with it. Raised for clinical review; it is not a rating.',
  style: { fontSize: '10.5px', fontWeight: 700, color: '#8f5312' },
};

// Unscored profile rows (threshold / follow-up): dashed separators, a small-caps
// "not scored" badge, grey answer chips.
export const UNSCORED_ROW_TOKENS = {
  separator: '#e4dbe8',
  chip: { bg: '#f4f2f6', fg: '#5c4f5f', border: '#ddd6e2' },
  badgeText: 'not scored',
};

// ---- DIFFERS (section 4.4) --------------------------------------------------
export const DIFFERS_BADGE_TOKENS = { bg: '#fef1e0', fg: '#8f5312', border: '#f3cfa0' };
// The candidate explanations render verbatim; the wording must not imply a cause.
export const DIFFERS_TOOLTIP =
  'Descriptive only — a marked home/school difference. Possible reasons: ' +
  'different demands, different support, tiredness, masking, or the two people ' +
  'seeing different things. It never changes a rating.';

// ---- core colours + type (section 5.5; existing design system values) -------
export const CORE_COLOURS = {
  ink: '#43213e',
  body: '#2c2230',
  secondary: '#5c4f5f',
  secondary2: '#6b6480',
  muted: '#9a8fa3',
  link: '#6a3d8f',
  linkHover: '#43213e',
  focusRing: '#7a5ca0',
  card: '#ffffff',
  border: '#e6e2ea',
  hairline: '#f1ecf3',
  lightPlum: '#efe9f6',
  appBg: '#eef0f4',
};

export const TYPE_SCALE = {
  fontFamily: "'Poppins', system-ui, sans-serif",
  h1: { size: 32, weight: 700 },
  areaH2: { size: 19, weight: 700 },
  body: { size: 14 }, // >=13px is a hard floor for body text
  label: { size: 13 },
  caption: { size: 12 },
  eyebrow: { size: 11, weight: 700, textTransform: 'uppercase' },
  bodyFloorPx: 13,
  chipSubTextMinPx: 11, // sub-text stays >=11px at >=80% opacity
};

export const RADII = { card: 18, panel: 13, chip: 10, pill: 999, swatch: 4 };

// Risk tiers keep their existing treatment — referenced here so no one
// "centralises" them into a restyle by accident (instruction section 5.1).
export const RISK_TIERS_DO_NOT_RESTYLE = Object.freeze({
  urgent: '#e5484d',
  high: '#ef8f3c',
  medium: '#e7bb3b',
  standard: '#3a9b4e',
  needReview: '#9aa0ab',
});
